import api from './client';

// Contract API - D/s relationship contracts
export const contractAPI = {
  // Contracts
  getAll: (status?: string) =>
    api.get('/contracts', { params: status ? { status } : undefined }),
  getMy: () => api.get('/contracts/my'),
  getDueForReview: () => api.get('/contracts/due-for-review'),
  getById: (id: number) => api.get(`/contracts/${id}`),
  // Bug-registry.md (found 2026-08-11, travel-bugfix-loop batch) — dominantId/
  // submissiveId are the UUID public ids from the org-member picker (the only
  // id shape the frontend has for another member — see OrgMember.userId in
  // types.ts); the backend resolves them to numeric ids via resolveUserId().
  // Was `number`, which forced useContracts.ts to `parseInt()` a UUID string
  // into a meaningless small integer before this method was ever called.
  create: (data: {
    title?: string;
    dominantId: string;
    submissiveId: string;
    dominantTitle?: string;
    submissiveTitle?: string;
    startDate?: string;
    endDate?: string;
    reviewPeriodDays?: number;
    preamble?: string;
    protocols?: string;
    communicationTerms?: string;
    amendmentProcess?: string;
    terminationTerms?: string;
    additionalTerms?: string;
  }) => api.post('/contracts', data),
  update: (id: number, data: Partial<{
    title: string;
    dominantTitle: string;
    submissiveTitle: string;
    startDate: string;
    endDate: string;
    reviewPeriodDays: number;
    preamble: string;
    protocols: string;
    communicationTerms: string;
    amendmentProcess: string;
    terminationTerms: string;
    additionalTerms: string;
  }>) => api.put(`/contracts/${id}`, data),
  sendForSignature: (id: number) => api.post(`/contracts/${id}/send`),
  // Accept/decline a pending contract invitation (Spec 303 Fix 4). Only the
  // named party who did NOT create the contract may call these.
  accept: (id: number) => api.post(`/contracts/${id}/accept`),
  decline: (id: number) => api.post(`/contracts/${id}/decline`),
  // Spec: red-team review 2026-08-09 (bug-registry.md 0zc), LOW — canonical
  // consent text for signing, generated server-side from the contract's actual
  // title/version/signer-role (contractRoutes.ts GET /:id/consent-text). Never
  // previously called by the frontend, which instead built its own hardcoded
  // string in useContracts.ts's handleSignContract.
  getConsentText: (id: number) => api.get(`/contracts/${id}/consent-text`),
  sign: (id: number, consentText: string) =>
    api.post(`/contracts/${id}/sign`, { consentText }),
  terminate: (id: number, reason?: string) =>
    api.post(`/contracts/${id}/terminate`, { reason }),
  createNewVersion: (id: number) => api.post(`/contracts/${id}/new-version`),
  delete: (id: number) => api.delete(`/contracts/${id}`),

  // Rules
  getRules: (contractId: number) => api.get(`/contracts/${contractId}/rules`),
  addRule: (contractId: number, data: {
    category?: string;
    ruleText: string;
    consequenceTemplateId?: number;
  }) => api.post(`/contracts/${contractId}/rules`, data),
  updateRule: (ruleId: number, data: Partial<{
    category: string;
    ruleText: string;
    consequenceTemplateId: number;
  }>) => api.put(`/contracts/rules/${ruleId}`, data),
  removeRule: (ruleId: number) => api.delete(`/contracts/rules/${ruleId}`),
  reorderRules: (contractId: number, ruleOrder: number[]) =>
    api.post(`/contracts/${contractId}/rules/reorder`, { ruleOrder }),

  // Amendments
  getAmendments: (contractId: number, status?: string) =>
    api.get(`/contracts/${contractId}/amendments`, { params: status ? { status } : undefined }),
  proposeAmendment: (contractId: number, data: {
    amendmentType: string;
    description: string;
    oldValue?: string;
    newValue?: string;
    effectiveDate?: string;
  }) => api.post(`/contracts/${contractId}/amendments`, data),
  approveAmendment: (amendmentId: number) =>
    api.post(`/contracts/amendments/${amendmentId}/approve`),
  rejectAmendment: (amendmentId: number, reason?: string) =>
    api.post(`/contracts/amendments/${amendmentId}/reject`, { reason }),
  withdrawAmendment: (amendmentId: number) =>
    api.post(`/contracts/amendments/${amendmentId}/withdraw`),

  // Reviews
  getReviews: (contractId: number) => api.get(`/contracts/${contractId}/reviews`),
  createReview: (contractId: number, data: {
    reviewDate?: string;
    dominantNotes?: string;
    submissiveNotes?: string;
    outcome?: string;
    nextReviewScheduled?: string;
  }) => api.post(`/contracts/${contractId}/reviews`, data),
  updateReview: (reviewId: number, data: Partial<{
    dominantNotes: string;
    submissiveNotes: string;
    outcome: string;
    nextReviewScheduled: string;
  }>) => api.put(`/contracts/reviews/${reviewId}`, data),

  // Premium: Suspension Management
  suspend: (id: number, data: { reason: string; endDate?: string }) =>
    api.post(`/contracts/${id}/suspend`, data),
  resume: (id: number) => api.post(`/contracts/${id}/resume`),

  // Cryptographic Signatures
  getSignatures: (contractId: number) =>
    api.get(`/contracts/${contractId}/signatures`),
  verifySignature: (contractId: number, signatureId: string) =>
    api.get(`/contracts/${contractId}/signatures/${signatureId}/verify`),

  // Premium: Multi-Submissive Management
  addSubmissive: (contractId: number, data: { submissiveId: string; title?: string }) =>
    api.post(`/contracts/${contractId}/submissives`, data),
  removeSubmissive: (contractId: number, submissiveId: string) =>
    api.delete(`/contracts/${contractId}/submissives/${submissiveId}`),
  // Spec: red-team review 2026-08-09 (bug-registry.md 0zc), HIGH — was posting to
  // `/contracts/${contractId}/enable-multi-submissive`, but the real backend route
  // (contractRoutes.ts) is `/:id/multi-submissive/enable` — guaranteed 404.
  enableMultiSubmissive: (contractId: number) =>
    api.post(`/contracts/${contractId}/multi-submissive/enable`),

  // Milestones (082-F)
  getMilestones: (contractId: number) =>
    api.get(`/contracts/${contractId}/milestones`),
  addMilestone: (contractId: number, data: {
    milestoneType: string;
    title: string;
    description?: string;
    triggerDate: string;
    action?: string;
  }) => api.post(`/contracts/${contractId}/milestones`, data),
  acknowledgeMilestone: (milestoneId: number) =>
    api.post(`/contracts/milestones/${milestoneId}/acknowledge`),
  deleteMilestone: (milestoneId: number) =>
    api.delete(`/contracts/milestones/${milestoneId}`),

  // Limits↔Contracts integration (082-J)
  // Same UUID-vs-numeric fix as `create` above.
  createFromNegotiation: (data: {
    negotiationId: number;
    title?: string;
    dominantId: string;
    submissiveId: string;
    dominantTitle?: string;
    submissiveTitle?: string;
    startDate?: string;
    endDate?: string;
    reviewPeriodDays?: number;
    preamble?: string;
    protocols?: string;
    communicationTerms?: string;
    amendmentProcess?: string;
    terminationTerms?: string;
    additionalTerms?: string;
  }) => api.post('/contracts/from-negotiation', data),
  getLimitsDrift: (contractId: number) =>
    api.get(`/contracts/${contractId}/limits-drift`),
};
